import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Reveal from '../components/ui/Reveal';
import API, { UPLOADS_URL } from '../api/axios';

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const paras = (text) => (text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

const DEFAULT_SECTIONS = [
  {
    section_key: 'hero',
    section_type: 'hero',
    image: '/images/page-banners/ags.webp',
    kicker: 'About St. Andrews Group',
    title: 'Seven Branches. A Shared Purpose. A Commitment to Every Child.',
  },
  {
    section_key: 'intro',
    section_type: 'intro',
    kicker: 'About St. Andrews Group',
    description:
      "Education's finest legacy is a generation with the courage to question, the integrity to lead and the compassion to serve.\n\nTo inspire every child to think fearlessly, learn with curiosity, lead with confidence and grow with compassion.",
  },
  {
    section_key: 'values_card_1',
    section_type: 'values_card',
    icon: 'fa-shield-alt',
    title: 'Integrity',
    description: 'Integrity, respect, compassion and intellectual curiosity guide our expectations of learning, conduct and relationships.',
  },
  {
    section_key: 'values_card_2',
    section_type: 'values_card',
    icon: 'fa-heart',
    title: 'Compassion',
    description: 'We encourage professional reflection, collaboration and continued growth among our educators, strengthening a community committed to thoughtful teaching and responsible mentorship.',
  },
  {
    section_key: 'values_card_3',
    section_type: 'values_card',
    icon: 'fa-brain',
    title: 'Intellectual Curiosity',
    description: 'Critical thinking, adaptability and ethical judgement equip learners to make considered decisions and navigate a world shaped by emerging technologies with confidence.',
  },
  {
    section_key: 'values_card_4',
    section_type: 'values_card',
    icon: 'fa-user-friends',
    title: 'Emotional Security',
    description: 'Our educators build relationships founded on empathy and trust, encouraging children to express concerns, seek reassurance and approach difficulties with renewed self-belief.',
  },
  {
    section_key: 'mission',
    section_type: 'mission',
    description:
      "Education's finest legacy is a generation with the courage to question, the integrity to lead and the compassion to serve.\n\nTo inspire every child to think fearlessly, learn with curiosity, lead with confidence and grow with compassion.\n\nAt St. Andrews World School, Indirapuram, our vision is to foster a truly holistic approach to education where academic excellence, emotional well-being, strong values, creativity and future-ready skills come together to shape well-rounded individuals.\n\nWe aspire to nurture confident, responsible and empathetic learners who are equipped to embrace a rapidly changing world, make thoughtful choices and contribute meaningfully to society.\n\nOur vision is to make every child's school journey a strong foundation for lifelong learning, leadership, character and meaningful success.",
  },
  {
    section_key: 'philosophy',
    section_type: 'philosophy',
    description:
      "St. Andrews World School's educational philosophy is to provide child-centric education through our uniquely crafted academics. Our modern schooling education equally focuses on the interactive and experiential part of learning.\n\nWe want to impart knowledge that can help them with real-world situations and not be just limited to the classroom. We have adopted modern schooling education where we work towards promoting the academic success of the student. We ensure that their every achievement is celebrated and they feel motivated.\n\nSt. Andrews World School dedicatedly works toward holistic development. Our core values work in synchronization with our philosophy of 'Education for all' \u2014 emphasizing the success of students.\n\nSt. Andrews World School has a student-centric curriculum, where the teachers and parents equally work for the student's academic prowess.",
  },
  {
    section_key: 'cta',
    section_type: 'cta',
    icon: 'fa-building-columns',
    title: 'Seven Branches. A Shared Purpose. A Commitment to Every Child.',
    description: 'Experience the philosophy and community of St. Andrews Group for yourself.',
    button_text: 'Contact Us',
    button_url: '/contact',
  },
];

const Paragraphs = ({ items, HighlightLast = false }) => (
  <div className="mt-6 space-y-5 text-body text-[17px] leading-relaxed">
    {items.map((p, i) => (
      <Reveal key={i} delayMs={i * 60}>
        {HighlightLast && i === items.length - 1 ? (
          <div className="card p-7 md:p-9 bg-primary text-white">
            <p className="text-white/90 leading-relaxed">{p}</p>
          </div>
        ) : (
          <p>{p}</p>
        )}
      </Reveal>
    ))}
  </div>
);

const SectionHeading = ({ kicker, title }) => (
  <Reveal>
    <div className="section-title">
      <div className="kicker">{kicker}</div>
      <h2 className="h-section text-3xl md:text-4xl font-bold">{title}</h2>
      <div className="divider-line" />
    </div>
  </Reveal>
);

const StAndrewsGroup = () => {
  const [sections, setSections] = useState(DEFAULT_SECTIONS);

  useEffect(() => {
    let mounted = true;
    const fetchSections = async () => {
      try {
        const res = await API.get('/about-standrews/sections');
        if (!mounted) return;
        if (res.data?.data?.length) setSections(res.data.data);
      } catch (err) {
        /* fall back to defaults */
      }
    };
    fetchSections();
    return () => { mounted = false; };
  }, []);

  const hero = sections.find((s) => s.section_type === 'hero');
  const intro = sections.find((s) => s.section_type === 'intro');
  const values = sections.filter((s) => s.section_type === 'values_card');
  const mission = sections.find((s) => s.section_type === 'mission');
  const philosophy = sections.find((s) => s.section_type === 'philosophy');
  const cta = sections.find((s) => s.section_type === 'cta');

  const introParas = paras(intro && intro.description);
  const missionParas = paras(mission && mission.description);
  const philosophyParas = paras(philosophy && philosophy.description);

  return (
    <>
      <SEO
        title="About St. Andrews Group of Schools"
        description="About St. Andrews Group of Schools — seven branches, a shared purpose and a commitment to every child."
        keywords="St Andrews Group, St Andrews World School branches, CISCE school India"
      />

      {/* HERO */}
      <section className="page-hero">
        <img
          src={resolveImage(hero && hero.image)}
          alt={(hero && hero.title) || 'About St. Andrews Group of Schools'}
          className="page-hero-img"
          loading="eager"
        />
        <div className="page-hero-content">
          <div className="container">
            <div className="page-hero-inner" />
          </div>
        </div>
      </section>

      {/* ABOUT ST. ANDREWS GROUP */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-[1000px] mx-auto text-center">
            <Reveal>
              <div className="kicker">{intro && (intro.kicker || 'About St. Andrews Group')}</div>
              <h2 className="h-section text-3xl md:text-4xl font-bold mt-2">
                {(hero && hero.title) || 'Seven Branches. A Shared Purpose. A Commitment to Every Child.'}
              </h2>
              <div className="divider-line mx-auto" />
            </Reveal>
            <Paragraphs items={introParas} HighlightLast />
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section bg-light">
        <div className="container">
          <SectionHeading kicker="What Guides Us" title="Our Values" />
          {values.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mt-10">
              {values.map((item, index) => (
                <Reveal key={item.section_key || item.title || index} delayMs={index * 80}>
                  <div className="card p-7 h-full group hover:shadow-lg transition-all duration-300 relative overflow-hidden text-center">
                    <div className="absolute -right-10 -top-10 w-28 h-28 rounded-full bg-secondary/15" aria-hidden />
                    <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-all">
                      <i className={`fas ${item.icon}`} aria-hidden />
                    </div>
                    <h3 className="text-primary font-bold text-lg md:text-xl leading-snug mt-5">{item.title}</h3>
                    {item.description && (
                      <p className="text-body text-[15px] leading-relaxed mt-3">{item.description}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* OUR MISSION */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-[980px] mx-auto">
            <SectionHeading kicker="What Drives Us" title="Our Mission" />
            <Paragraphs items={missionParas} />
          </div>
        </div>
      </section>

      {/* OUR PHILOSOPHY */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-[980px] mx-auto">
            <SectionHeading kicker="Our Approach" title="Our Philosophy" />
            <Paragraphs items={philosophyParas} HighlightLast />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#1B3260] relative overflow-hidden text-white">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '24px 24px' }} aria-hidden />
        <div className="container relative">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <i className={`fas ${(cta && cta.icon) || 'fa-building-columns'} mx-auto mb-6 text-4xl text-[#F3D27E]`} aria-hidden />
              <h2 className="text-[26px] md:text-[34px] font-bold text-white leading-tight">
                {(cta && cta.title) || 'Seven Branches. A Shared Purpose. A Commitment to Every Child.'}
              </h2>
              <p className="text-white/70 text-[16px] md:text-[17px] leading-relaxed mt-4">
                {(cta && cta.description) || 'Experience the philosophy and community of St. Andrews Group for yourself.'}
              </p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
                <Link to={(cta && cta.button_url) || '/contact'} className="btn-gold">
                  {(cta && cta.button_text) || 'Contact Us'}
                  <i className="fas fa-arrow-right text-[12px]" aria-hidden />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default StAndrewsGroup;